import SDKAdapter from '../core/adapter.js';
import { SDKModel } from '../core/model.js';
import { SDKModelSchema } from '../core/schema.js';
import datasourceSchema from '../schemas/datasource.js';
import { nanoid } from '../core/transport.js';
import { isDeepEquals, isObject, objectKeysShallowCompare } from '../utils/object.js';
export function DataSettings(settings) {
    const headers = {};
    for (const key in (settings === null || settings === void 0 ? void 0 : settings.headers) || {}) {
        if (settings.headers.hasOwnProperty(key)) {
            const headerCase = key.replace(/^[a-z]|\-[a-z]/g, (m) => m.toUpperCase());
            headers[headerCase] = settings.headers[key];
        }
    }
    if (!headers.Accept)
        headers['Accept'] = 'application/json';
    if (!headers['Content-Type'])
        headers['Content-Type'] = 'application/json';
    return {
        params: (settings === null || settings === void 0 ? void 0 : settings.params) || {},
        headers: headers,
        jsonpath: String((settings === null || settings === void 0 ? void 0 : settings.jsonpath) || '$'),
        method: ((settings === null || settings === void 0 ? void 0 : settings.method) || 'GET').toUpperCase(),
        body: (settings === null || settings === void 0 ? void 0 : settings.body) || {},
        url: String((settings === null || settings === void 0 ? void 0 : settings.url) || '')
    };
}
export class Datasources extends SDKAdapter {
    fetch(datasource) {
        const { libraryId } = datasource;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/datasources`);
    }
    get(datasource) {
        const { libraryId, id } = datasource;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/datasources/${id}`);
    }
    async fetchUpdates(datasource) {
        const [url, options] = toFetchArguments(datasource.settings);
        const sample = await this.sdk.proxyJSON(url, options);
        return { ...datasource, sample, sampledAt: new Date() };
    }
    post(datasource) {
        const { libraryId } = datasource;
        const definition = datasource.isInternal() ? datasource.sample : datasource.definition;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/datasources`, {
            method: 'POST',
            body: JSON.stringify({
                ...datasource,
                definition
            })
        });
    }
    put(datasource) {
        return this.post(datasource);
    }
    delete(datasource) {
        const { libraryId, id } = datasource;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/datasources/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'text/plain' }
        });
    }
}
export class XmDatasources extends Datasources {
    async fetch(datasource) {
        const { libraryId, externalSourceId } = datasource;
        if (!externalSourceId)
            return [];
        return await this.sdk.fetchJSON(`/libraries/${libraryId}/tenants/${externalSourceId}/datasources`);
    }
    async fetchUpdates(datasource) {
        const { id, libraryId, externalSourceId } = datasource;
        if (!externalSourceId)
            return null;
        return await this.sdk.fetchJSON(`/libraries/${libraryId}/tenants/${externalSourceId}/datasources/${id}/fetch-updates`);
    }
}
export class ContentHubOneDatasources extends Datasources {
    async fetch(datasource) {
        const { libraryId } = datasource;
        return await this.sdk.fetchJSON(`/libraries/${libraryId}/datasources/content-hub-one`);
    }
    async fetchUpdates(datasource) {
        const { id, libraryId } = datasource;
        return await this.sdk.fetchJSON(`/libraries/${libraryId}/datasources/content-hub-one/${id}/fetch-updates`);
    }
}
export function getDatasourceDefaults(sdk) {
    const now = new Date();
    return {
        id: nanoid(10),
        sampledAt: null,
        createdAt: now,
        modifiedAt: now,
        type: 'internal',
        definition: {},
        conflictDefinition: null,
        settings: DataSettings({})
    };
}
export class DatasourceModel extends SDKModel {
    get adapter() {
        if (this.type === 'xmTemplate')
            return this.sdk.XmDatasources;
        else if (this.type === 'contentHubOne')
            return this.sdk.ContentHubOneDatasources;
        else
            return this.sdk.Datasources;
    }
    async fetchUpdates() {
        return this.change(await this.adapter.fetchUpdates(this));
    }
    getCollection() {
        return this.type === 'internal' || !this.type
            ? this.library.datasources
            : this.type === 'contentHubOne'
                ? this.library.contentHubOneDatasources
                : this.sdk.auth.tenant.datasources;
    }
    getHiddenProps() {
        return ['library'];
    }
    getDefaults(sdk) {
        return getDatasourceDefaults(sdk);
    }
    isInternal() {
        return this.type === 'internal';
    }
    isExtended() {
        return this.sdk.renderingHost.registeredDatasources.some((e) => e.id === this.id && !e.isEqual(this));
    }
    static loadFromSessionStorage() {
        return JSON.parse(sessionStorage.getItem('feaas-external-datasources'));
    }
    static storeToSessionStorage(datasources) {
        if (typeof sessionStorage != 'undefined')
            sessionStorage.setItem('feaas-external-datasources', JSON.stringify(datasources));
    }
    static setExternalDatasources(datasources) {
        this.registeredDatasources = datasources;
    }
    static updateExternalDatasources(datasources, list = this.registeredDatasources) {
        datasources.setItems(list.map((item) => {
            return {
                settings: DataSettings({}),
                type: 'external',
                sample: item.sample,
                ...item
            };
        }));
    }
    getPath() {
        return `/libraries/${this.libraryId}/datasources/${this.id}`;
    }
    isConflictingTowards(previousState) {
        return DatasourceModel.isConflicting(this.sample, previousState.sample);
    }
    /**
     * Looks for changes in the generated sample of the definition of a datasource. It only looks for removal of fields
     * though. Addition of fields is not something that concerns us
     */
    static isConflicting(currentSample, previousSample) {
        // if no changes at all, return early
        if (isDeepEquals(currentSample, previousSample))
            return false;
        // if both are arrays, just return true (do not proceed to fields checking). In case of root type change, return false
        if (Array.isArray(currentSample)) {
            return Array.isArray(previousSample);
        }
        // recursively check the object for deletion of fields
        const check = (previousSample, currentSample) => {
            for (const key in previousSample) {
                if (!(currentSample === null || currentSample === void 0 ? void 0 : currentSample[key]))
                    return true;
                else if (isObject(previousSample[key])) {
                    if (check(previousSample[key], currentSample[key]))
                        return true;
                }
            }
            return false;
        };
        return check(previousSample, currentSample);
    }
    static getTreeViewLeafState(previous, current) {
        if (!(previous === null || previous === void 0 ? void 0 : previous.length) && (current === null || current === void 0 ? void 0 : current.length)) {
            return 'new';
        }
        else if (!(current === null || current === void 0 ? void 0 : current.length) && (previous === null || previous === void 0 ? void 0 : previous.length)) {
            return 'removed';
        }
        else if (!objectKeysShallowCompare(previous, current)) {
            return 'changed';
        }
        else {
            return 'unchanged';
        }
    }
    static getSettingsFromDatasources(datasourceIds, datasources) {
        if (datasourceIds.every((id) => { var _a; return ((_a = datasources.find((d) => d.id == id)) === null || _a === void 0 ? void 0 : _a.type) == 'xmTemplate'; }))
            return null;
        return datasources
            .filter((d) => datasourceIds.find((id) => d.id == id))
            .reduce((map, datasource) => {
            return Object.assign(map, {
                [datasource.id]: datasource.settings.url ? datasource.settings : datasource.sample
            });
        }, {});
    }
    static getExternalComponentDatasources(data, allDatasources) {
        const datasourceIds = data ? Object.keys(data).filter((key) => data[key]) : [];
        return (allDatasources === null || allDatasources === void 0 ? void 0 : allDatasources.filter((d) => datasourceIds.includes(d.id))) || [];
    }
    static get schema() {
        return new SDKModelSchema(DatasourceModel, datasourceSchema);
    }
    async copyToLibrary(library) {
        return library.datasources.upsertItem(this).save();
    }
}
/**
 * Converts settings object into fetch function arguments.
 *
 * @param {any} settings  - The settings object.
 * @returns {Array} - An array containing URL and options objects as fetch function arguments.
 */
export function toFetchArguments(settings) {
    const { url, headers, params, method, body: payload } = DataSettings(settings);
    let body = undefined;
    const contentType = headers['Content-Type'];
    if (method != 'GET' && method != 'HEAD') {
        if (contentType === 'application/json')
            body = JSON.stringify(payload);
        if (contentType === 'multipart/form-data')
            body = Object.keys(payload).reduce((form, key) => {
                form.append(key, payload[key]);
                return form;
            }, new FormData());
        if (contentType === 'application/x-www-form-urlencoded')
            body = new URLSearchParams(payload);
    }
    const query = Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
    return [
        `${url}${query}`,
        {
            headers,
            body,
            method
        }
    ];
}
//# sourceMappingURL=datasources.js.map